
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { featuredArticles, recentArticles } from '../../../mocks/articles';

export default function ArticleDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [allArticles, setAllArticles] = useState([]);
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);

  useEffect(() => {
    // ローカルストレージから投稿された記事を取得
    const userArticles = JSON.parse(localStorage.getItem('userArticles') || '[]');
    // 特定の記事を除外（タイトルで判定）
    const filteredUserArticles = userArticles.filter(article => 
      article.title !== 'AIとHologramが描く未来のテクノロジー最前線'
    );
    const combinedArticles = [...featuredArticles, ...recentArticles, ...filteredUserArticles];
    setAllArticles(combinedArticles);

    // ローカルストレージも更新
    localStorage.setItem('userArticles', JSON.stringify(filteredUserArticles));

    // 記事を検索
    const foundArticle = combinedArticles.find(a => a.id === parseInt(id || '1'));
    setArticle(foundArticle);

    // 関連記事を設定
    const related = combinedArticles.filter(a => a.id !== parseInt(id || '1')).slice(0, 3);
    setRelatedArticles(related);
  }, [id]);

  if (!article) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">記事が見つかりません</h1>
          <button 
            onClick={() => navigate('/media')}
            className="text-blue-600 hover:text-blue-700 cursor-pointer"
          >
            メディアホームに戻る
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gray-900 text-white py-4">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <button 
                onClick={() => navigate('/')}
                className="text-2xl font-bold hover:text-blue-400 transition-colors cursor-pointer"
              >
                AI Code グループ
              </button>
              <span className="text-blue-400 text-lg">MEDIA</span>
            </div>
            
            <nav className="hidden md:flex items-center space-x-6">
              <button 
                onClick={() => navigate('/media')}
                className="hover:text-blue-400 transition-colors cursor-pointer"
              >
                ホーム
              </button>
              <button 
                onClick={() => navigate('/media/articles')}
                className="hover:text-blue-400 transition-colors cursor-pointer"
              >
                記事一覧
              </button>
              <button 
                onClick={() => navigate('/media/post')}
                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                記事を投稿
              </button>
              <button 
                onClick={() => navigate('/admin/login')}
                className="bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                管理者
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="bg-white py-4">
        <div className="max-w-4xl mx-auto px-4">
          <nav className="flex items-center space-x-2 text-sm text-gray-600">
            <button onClick={() => navigate('/media')} className="hover:text-blue-600 cursor-pointer">ホーム</button>
            <i className="ri-arrow-right-s-line"></i>
            <button onClick={() => navigate('/media/articles')} className="hover:text-blue-600 cursor-pointer">記事一覧</button>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-900">{article.title}</span>
          </nav>
        </div>
      </div>

      {/* Article Content */}
      <article className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          {/* Article Header */}
          <header className="mb-8">
            <div className="flex items-center space-x-4 mb-4">
              <span className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm font-medium">
                {article.category}
              </span>
              <span className="text-gray-500">{article.date}</span>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
              {article.title}
            </h1>
            
            <div className="flex items-center space-x-6 text-sm text-gray-600 mb-8">
              <div className="flex items-center">
                <i className="ri-user-line mr-2"></i>
                <span>{article.author}</span>
              </div>
              {article.views && (
                <div className="flex items-center">
                  <i className="ri-eye-line mr-2"></i>
                  <span>{article.views.toLocaleString()} 回閲覧</span>
                </div>
              )}
              <div className="flex items-center">
                <i className="ri-time-line mr-2"></i>
                <span>約5分で読めます</span>
              </div>
            </div>
          </header>

          {/* Featured Image */}
          <div className="mb-8">
            <img 
              src={article.mainImage || article.image}
              alt={article.title}
              className="w-full h-96 object-cover object-center rounded-xl shadow-lg"
            />
          </div>

          {/* Article Body */}
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              {article.excerpt}
            </p>

            {/* 投稿された記事の場合はcontentを表示、既存記事の場合はデフォルトコンテンツ */}
            {article.content ? (
              <div className="text-gray-700 mb-6 leading-relaxed whitespace-pre-wrap">
                {article.content}
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">はじめに</h2>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  近年、AI技術とホログラム技術の融合により、従来では考えられなかった革新的なソリューションが次々と生まれています。
                  本記事では、最新の技術動向と実際の活用事例について詳しく解説いたします。
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">技術の概要</h2>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  AIとホログラム技術の組み合わせは、単なる映像表示を超えた双方向性のあるコミュニケーションを可能にします。
                  リアルタイムでの音声認識、自然言語処理、そして3Dホログラム投影技術が統合されることで、まるで実在の人物と会話しているかのような体験を提供できます。
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">実際の活用事例</h2>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  様々な業界で実用化が進んでおり、特に接客業、教育、医療分野での導入が注目されています。
                  24時間対応の受付業務や、専門知識を持つバーチャルアシスタントとしての活用など、人手不足の解決策としても期待されています。
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">今後の展望</h2>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  技術の進歩とコストの低下により、今後数年間でより幅広い分野での活用が期待されます。
                  特に、5G通信技術の普及により、リモートでのホログラム通信も現実的になってきており、働き方やコミュニケーションの在り方に大きな変革をもたらす可能性があります。
                </p>
              </>
            )}
          </div>

          {/* Share Buttons */}
          <div className="bg-white p-6 rounded-xl shadow-lg mb-12">
            <h3 className="text-lg font-bold text-gray-900 mb-4">この記事をシェア</h3>
            <div className="flex space-x-4">
              <button className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-full flex items-center cursor-pointer whitespace-nowrap">
                <i className="ri-twitter-line mr-2"></i>
                Twitter
              </button>
              <button className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-full flex items-center cursor-pointer whitespace-nowrap">
                <i className="ri-facebook-line mr-2"></i>
                Facebook
              </button>
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-full flex items-center cursor-pointer whitespace-nowrap">
                <i className="ri-linkedin-line mr-2"></i>
                LinkedIn
              </button>
              <button className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-full flex items-center cursor-pointer whitespace-nowrap">
                <i className="ri-link mr-2"></i>
                リンクをコピー
              </button>
            </div>
          </div>

          {/* Author Bio */}
          <div className="bg-white p-6 rounded-xl shadow-lg mb-12">
            <div className="flex items-start space-x-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-user-line text-2xl text-blue-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{article.author}</h3>
                <p className="text-gray-600 leading-relaxed">
                  AI・ホログラム技術の専門家として、最新技術の研究開発に従事。
                  業界の技術動向と実用化に向けた取り組みについて定期的に執筆しています。
                </p>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">関連記事</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {relatedArticles.map((relatedArticle) => (
              <article key={relatedArticle.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all transform hover:scale-105">
                <div 
                  onClick={() => navigate(`/media/articles/${relatedArticle.id}`)}
                  className="cursor-pointer"
                >
                  <img 
                    src={relatedArticle.mainImage || relatedArticle.image}
                    alt={relatedArticle.title}
                    className="w-full h-48 object-cover object-center"
                  />
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="bg-blue-100 text-blue-600 px-2 py-1 rounded-full text-xs font-medium">
                        {relatedArticle.category}
                      </span>
                      <span className="text-gray-500 text-xs">{relatedArticle.date}</span>
                    </div>
                    <h3 className="font-bold text-lg mb-2 leading-tight hover:text-blue-600 transition-colors">
                      {relatedArticle.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {relatedArticle.excerpt.substring(0, 100)}...
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <h3 className="text-2xl font-bold mb-4">HOLOGRAM MEDIA</h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                AIとホログラム技術の最新情報をお届けするオウンドメディアです。
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">コンテンツ</h4>
              <ul className="space-y-2 text-gray-300">
                <li><button onClick={() => navigate('/media/articles')} className="hover:text-white cursor-pointer">記事一覧</button></li>
                <li><button onClick={() => navigate('/media/post')} className="hover:text-white cursor-pointer">記事投稿</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">リンク</h4>
              <ul className="space-y-2 text-gray-300">
                <li><button onClick={() => navigate('/')} className="hover:text-white cursor-pointer">メインサイト</button></li>
                <li><a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Made with Readdy</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 AI Code グループ MEDIA. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
